import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Movement,
  Partner,
  Range,
  SelectorOption,
  Transaction,
  HistoryMovement,
} from 'types';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';
import asyncActionSwal from 'utils/asyncActionSwal';
import { getNewOperationDate } from 'utils/getNewOperationDate';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import { cleanValue } from 'constants/formats';

import { addPartnerOption } from 'constants/addPartnerOption';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetPartnerOptionsForOperationModal } from 'modules/transactions/hooks/useGetPartnerOptionsForOperationModal';
import { useGetHoldingClassOptionsForOperationModal } from 'modules/transactions/hooks/useGetHoldingClassOptionsForOperationModal';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { useGetAvailableRanges } from 'modules/_shared/hooks/useGetAvailableRanges';

import { SharesTransferModalContextType } from '../types/SharesTransferModalContextType';
import { sharesTransferModalInitValue } from '../constants/sharesTransferModalInitValue';
import { getSharesTransferLabels } from '../../../../../transactions/utils/getSharesTransferLabels';
import { useGetPriceTypeOptions } from '../hooks/useGetPriceTypeOptions';

import { getSharePrice } from '../utils/getSharePrice';
import { getMovements } from '../utils/getMovements';
import { getHistoryMovements } from '../utils/getHistoryMovements';
import { getTransactions } from '../utils/getTransactions';
import {
  getHoldingClassDisplayName,
  mapHoldingClassOptionForDisplay,
  mapHoldingClassOptionsForDisplay,
} from '../utils/formatHoldingClassOptionLabel';
import {
  mapPartnerOptionForDisplay,
  mapPartnerOptionsForDisplay,
} from '../utils/formatPartnerOptionLabel';
import { useGetAliasOptions } from '../../CapitalDecreaseModal/hooks/useAliasOptions';

const SharesTransferModalContext =
  createContext<SharesTransferModalContextType>(sharesTransferModalInitValue);

export const SharesTransferModalProvider: FC<{
  children: ReactNode;
  operationType: string;
}> = ({ children, operationType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const { data: priceTypeOptionsData, actions: priceTypeOptionsActions } =
    useGetPriceTypeOptions({
      operationType,
    });

  const { data: aliasData, actions: aliasActions } = useGetAliasOptions();

  const modalLabels = getSharesTransferLabels(operationType);

  const [scriptureView, setScriptureView] = useState<boolean>(true);
  const [date, setDate] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');

  const [selectedPartnerSeller, setSelectedPartnerSeller] =
    useState<string>('');
  const [selectedPartnerBuyer, setSelectedPartnerBuyer] = useState<string>('');
  const [newPartner, setNewPartner] = useState<Partner | null>(null);
  const [price, setPrice] = useState<number>(0);
  const [shareFrom, setShareFrom] = useState<number>(0);
  const [shareTo, setShareTo] = useState<number>(0);

  const [expandHistoryView, setExpandHistoryView] = useState<boolean>(false);
  const [expandHistoryViewDirection, setExpandHistoryViewDirection] =
    useState<string>('down');
  const [showAddPartnerSection, setShowAddPartnerSection] =
    useState<boolean>(false);
  const [unsavedShares, setUnsavedShares] = useState<number>(0);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [historyMovements, setHistoryMovements] = useState<HistoryMovement[]>(
    []
  );
  const [rangesPage, setRangesPage] = useState<number>(1);
  const [rangesRowsPerPage, setRangesRowsPerPage] = useState<number>(10);
  const [isValidatingRange, setIsValidatingRange] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptionsForOperationModal({
      societyId: actualSociety?.['_id'],
      includeSharesCountFirst: true,
      includeSharesCountSecond: true,
      newPartnerSecond: newPartner,
      onlyActiveFirst: true,
    });
  const partnerSellerOptions = useMemo(
    () => mapPartnerOptionsForDisplay(partnerOptionsData.partnerFirstOptions),
    [partnerOptionsData.partnerFirstOptions]
  );

  const partnerBuyerOptions = useMemo(
    () => mapPartnerOptionsForDisplay(partnerOptionsData.partnerSecondOptions),
    [partnerOptionsData.partnerSecondOptions]
  );

  const selectedPartnerSellerOption = useMemo(
    () =>
      mapPartnerOptionForDisplay(partnerOptionsData.selectedPartnerFirstOption),
    [partnerOptionsData.selectedPartnerFirstOption]
  );

  const selectedPartnerBuyerOption = useMemo(
    () =>
      mapPartnerOptionForDisplay(
        partnerOptionsData.selectedPartnerSecondOption
      ),
    [partnerOptionsData.selectedPartnerSecondOption]
  );

  const {
    handleChangeSelectedPartnerFirstOption:
      handleChangeSelectedPartnerSellerOption,
    handleChangeSelectedPartnerSecondOption:
      handleChangeSelectedPartnerBuyerOption,
  } = partnerOptionsActions;

  const handleSearchPartnerByTerm = async (
    searchTerm: string,
    firstPartner?: boolean
  ) => {
    const options = await partnerOptionsActions.fetchPartnersByTerm(
      searchTerm,
      firstPartner
    );

    if (!options) return options;

    return mapPartnerOptionsForDisplay(options);
  };

  const { data: holdingClassOptionsData, actions: holdingClassOptionsActions } =
    useGetHoldingClassOptionsForOperationModal({
      societyId: actualSociety?._id || '',
      includeShares: true,
    });

  const holdingClassOptions = useMemo(
    () =>
      mapHoldingClassOptionsForDisplay(
        holdingClassOptionsData.holdingClassOptions
      ),
    [holdingClassOptionsData.holdingClassOptions]
  );

  const selectedHoldingClassOption = useMemo(
    () =>
      mapHoldingClassOptionForDisplay(
        holdingClassOptionsData.selectedHoldingClassOption
      ),
    [holdingClassOptionsData.selectedHoldingClassOption]
  );

  const handleFetchHoldingClassByTerm = async (searchTerm: string) => {
    const options =
      await holdingClassOptionsActions.handleFetchHoldingClassByTerm(
        searchTerm
      );

    if (!options) return options;

    return mapHoldingClassOptionsForDisplay(options);
  };

  const {
    data: validRangesData,
    actions: validRangesActions,
    isLoading: isLoadingAvailableRanges,
  } = useGetAvailableRanges({
    partnerId: selectedPartnerSeller,
    holdingClassId: holdingClassOptionsData.selectedHoldingClass,
    page: rangesPage,
    rowsPerPage: rangesRowsPerPage,
  });

  const handleResetValues = () => {
    validRangesActions.handleResetValues();
    setUnsavedShares(0);
    setShareFrom(0);
    setShareTo(0);
  };

  const handleChangeRangesPage = (newPage: number) => {
    setRangesPage(newPage);
  };

  const handleChangeRangesRowsPerPage = (newRowsPerPage: number) => {
    setRangesRowsPerPage(newRowsPerPage);
    setRangesPage(1);
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const onChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const onChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const onChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangePartnerSeller = (selectedOption: SelectorOption | null) => {
    if (validRangesData.validatedRanges.length > 0) {
      asyncActionSwal({
        swalTitle: t('ValidatedRangesWillBeLostWhenChangingPartner'),
        swalElementName: '',
        swalInfoText: '',
        swalConfirmButtonText: t('Continue'),
        callback: () => {
          handleResetValues();
          setSelectedPartnerSeller(selectedOption?.value || '');
          handleChangeSelectedPartnerSellerOption(selectedOption);
        },
      });
    } else {
      handleResetValues();
      setSelectedPartnerSeller(selectedOption?.value || '');
      handleChangeSelectedPartnerSellerOption(selectedOption);
    }
  };

  const handleChangePartnerBuyer = (selectedOption: SelectorOption | null) => {
    setSelectedPartnerBuyer(selectedOption?.value || '');
    handleChangeSelectedPartnerBuyerOption(selectedOption);
    setShowAddPartnerSection(selectedOption?.value === addPartnerOption.value);
  };

  const handleAddPartner = (addedPartner: Partner) => {
    setNewPartner(addedPartner);
  };

  const handleCloseAddPartnerSection = () => {
    if (selectedPartnerBuyer === addPartnerOption.value) {
      setSelectedPartnerBuyer('');
      handleChangeSelectedPartnerBuyerOption(null);
    }
    setShowAddPartnerSection(false);
  };

  const handleChangeHoldingClass = (selectedOption: SelectorOption) => {
    if (validRangesData.validatedRanges.length > 0) {
      asyncActionSwal({
        swalTitle: t('ValidatedRangesWillBeLostWhenChangingClass'),
        swalElementName: '',
        swalInfoText: '',
        swalConfirmButtonText: t('Continue'),
        callback: () => {
          handleResetValues();
          holdingClassOptionsActions.handleClassOptionChange(selectedOption);
        },
      });
    } else {
      handleResetValues();
      holdingClassOptionsActions.handleClassOptionChange(selectedOption);
    }
  };

  const handleChangeShareFrom = (event: ChangeEvent<HTMLInputElement>) => {
    const value = cleanValue(event.target.value);
    setShareFrom(value);
  };

  const handleChangeShareTo = (event: ChangeEvent<HTMLInputElement>) => {
    const value = cleanValue(event.target.value);
    setShareTo(value);
  };

  const handleChangePrice = (event: ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = cleanValue(event.target.value);
    setPrice(cleanedValue);
  };

  const handleChangeExpandHistoryView = (): void => {
    setExpandHistoryView(!expandHistoryView);
    setExpandHistoryViewDirection(expandHistoryView ? 'down' : 'up');
  };

  const handleClickRange = (range: Range) => {
    setShareFrom(range.from);
    setShareTo(range.to);
  };

  const calculateSharePrice = () =>
    getSharePrice({
      price,
      priceType: priceTypeOptionsData.selectedPriceType || '',
      sharesToTransfer: unsavedShares,
      societySharesCount: actualSociety?.sharesCount?.actual,
    });

  const updateMovements = (
    rangesToAddToSeller: Range[],
    rangesToRemoveFromSeller: Range[],
    rangesToAddToBuyer: Range[]
  ) => {
    const movements: Movement[] = getMovements({
      rangesToAddToSeller,
      rangesToRemoveFromSeller,
      rangesToAddToBuyer,
      partnerSellerId: selectedPartnerSeller,
      partnerBuyerId: selectedPartnerBuyer,
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
    });
    setMovements((prev: Movement[]) => [...prev, ...movements]);
  };

  const updateHistoryMovements = (sharePrice: number) => {
    const historyMovements: HistoryMovement[] = getHistoryMovements({
      validatedRanges: validRangesData.validatedRanges,
      holdingClassName: getHoldingClassDisplayName(
        holdingClassOptionsData.selectedHoldingClassOption
      ),
      partnerNameSeller:
        partnerOptionsData.selectedPartnerFirstOption?.label
          .split('|')[1]
          .trim() || '',
      partnerNameBuyer:
        partnerOptionsData.selectedPartnerSecondOption?.label
          .split('|')[1]
          .trim() || '',
      sharePrice,
    });
    setHistoryMovements((prev: HistoryMovement[]) => [
      ...historyMovements,
      ...prev,
    ]);
  };

  const updateTransactions = (sharePrice: number) => {
    const transactions: Transaction[] = getTransactions({
      ranges: validRangesData.validatedRanges,
      partnerSellerId: selectedPartnerSeller,
      partnerBuyerId: selectedPartnerBuyer,
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
      sharePrice,
    });
    setTransactions((prev: Transaction[]) => [...prev, ...transactions]);
  };

  const handleValidateRange = async () => {
    try {
      setIsValidatingRange(true);

      const range = {
        from: shareFrom,
        to: shareTo,
        partner: selectedPartnerSeller,
        shareClass: holdingClassOptionsData.selectedHoldingClass,
      };

      const isValidRange = await validRangesActions.handleValidateRange(range);

      setIsValidatingRange(false);

      if (!isValidRange) return;

      setUnsavedShares((prev) => prev + shareTo - shareFrom + 1);

      setShareFrom(0);
      setShareTo(0);
    } catch (error) {
      setIsValidatingRange(false);
    }
  };

  const handleSaveMovement = () => {
    const rangesToAddToSeller = validRangesData.generatedRanges;
    const rangesToRemoveFromSeller = validRangesData.removedRanges;
    const rangesToAddToBuyer = validRangesData.validatedRanges;

    const sharePrice = calculateSharePrice();

    updateMovements(
      rangesToAddToSeller,
      rangesToRemoveFromSeller,
      rangesToAddToBuyer
    );
    updateHistoryMovements(sharePrice);
    updateTransactions(sharePrice);

    validRangesActions.handleSaveMovement();
    setUnsavedShares(0);
    setShareFrom(0);
    setShareTo(0);
  };

  const handleSaveSharesTransfer = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);

        let uploadedDocument = null;
        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: file.name,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }
        const newOperationDate = getNewOperationDate(lastOperationDate, date);
        const sharesTransferData = {
          movements,
          transactions,
          date: newOperationDate,
          nominalValue: actualSociety.nominalValue,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          notarialRegistration: {
            notaryName,
            notaryCity,
            protocolNr: notaryProtocol,
          },
          operationType,
          alias: aliasData.selectedAlias || '',
        };
        await store.dispatch(addOperation(sharesTransferData));
        setIsLoading(false);
        const successMessage: { [key: string]: string } = {
          SELL_PARTICIPATION: t('SellSuccessfullyCompleted'),
          HERITAGE: t('HeritageSuccessfullyCompleted'),
          DONATION: t('DonationSuccessfullyCompleted'),
          ENDORSEMENT: t('EndorsementSuccessfullyCompleted'),
          TRANSMISSION: t('TransmissionSuccessfullyCompleted'),
        };
        successSwal(successMessage[operationType]);
      } catch (error) {
        const errorMessage: { [key: string]: string } = {
          SELL_PARTICIPATION: t('ErrorWhenPerformingSellParticipation'),
          HERITAGE: t('ErrorWhenPerformingHeritage'),
          DONATION: t('ErrorWhenPerformingDonation'),
          ENDORSEMENT: t('ErrorWhenPerformingEndorsement'),
          TRANSMISSION: t('ErrorWhenPerformingTransmission'),
        };
        errorSwal(errorMessage[operationType]);
      } finally {
        handleCloseModal();
      }
    }
  };

  return (
    <SharesTransferModalContext.Provider
      value={{
        data: {
          operationType,
          modalLabels,
          date,
          societyActualShares: actualSociety?.sharesCount?.actual || 0,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          aliasOptions: aliasData.aliasOptions,
          scriptureView,
          selectedPartnerSellerOption,
          partnerSellerOptions,
          selectedPartnerBuyerOption,
          partnerBuyerOptions,
          selectedHoldingClass: holdingClassOptionsData.selectedHoldingClass,
          holdingClassOptions,
          selectedHoldingClassOption,
          shareFrom,
          shareTo,
          validatedRanges: validRangesData.validatedRanges,
          rangesPage,
          rangesRowsPerPage,
          availableRanges: validRangesData.availableRanges,
          totalAvailableRanges: validRangesData.totalAvailableRanges,
          price,
          selectedPriceTypeOption: priceTypeOptionsData.selectedPriceTypeOption,
          priceTypeOptions: priceTypeOptionsData.priceTypeOptions,
          lastOperationDate: formattedLastOperationDate,
          historyMovements,
          expandHistoryView,
          expandHistoryViewDirection,
          showAddPartnerSection,
          isValidRange: validRangesData.isValidRange,
          isValidatingRange,
          isLoadingAvailableRanges,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          onChangeNotaryName,
          onChangeNotaryProtocol,
          onChangeNotaryCity,
          handleChangeAlias: aliasActions.handleChangeAlias,
          handleChangePartnerSeller,
          handleChangePartnerBuyer,
          handleSearchPartnerByTerm,
          handleChangeHoldingClass,
          handleFetchHoldingClassByTerm,
          handleValidateRange,
          handleChangeShareFrom,
          handleChangeShareTo,
          handleChangePrice,
          handleChangePriceType:
            priceTypeOptionsActions.handleChangePriceTypeOption,
          handleChangeRangesPage,
          handleChangeRangesRowsPerPage,
          handleClickRange,
          handleChangeExpandHistoryView,
          handleAddPartner,
          handleCloseAddPartnerSection,
          handleSaveMovement,
          handleSaveSharesTransfer,
        },
      }}
    >
      {children}
    </SharesTransferModalContext.Provider>
  );
};

export const useSharesTransferModal = () => {
  const context = useContext(SharesTransferModalContext);
  if (!context) {
    throw new Error(
      'useSharesTransfer must be used within a SharesTransferModalProvider'
    );
  }
  return context;
};
